<template>
  <div
    :class="[
      'ds-avatar',
      `ds-avatar-${mask}`,
      `ds-avatar-${size}`,
      { 'ds-avatar-letter': !src && !populating },
    ]"
    data-testid="ds-avatar"
  >
    <DsImageCloud
      v-if="src"
      :src="src"
      :alt="alt"
      class="ds-avatar-image-cloud"
      :mask="mask"
      populating-class="skeleton-circle"
      :populating="populating"
    />
    <template v-else>
      <span
        v-if="!populating"
        class="ds-avatar-letter"
        data-testid="ds-avatar-letter"
      >
        {{ firstLetter }}
      </span>
      <DsSkeleton
        v-else
        class="ds-avatar-skeleton"
        :mask="mask"
        data-testid="ds-avatar-skeleton"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { DsImageCloud } from "../image-cloud";
import type { PropType } from "vue";

import DsSkeleton from "../skeleton/DsSkeleton.vue";

import type { DsMaskType, DsThemeSize } from "../../types";

defineOptions({
  name: "DsAvatar",
});

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  alt: {
    type: String,
    required: true,
  },
  mask: {
    type: String as PropType<DsMaskType>,
    default: "circle",
  },
  size: {
    type: String as PropType<DsThemeSize>,
    default: "md",
  },
  populating: {
    type: Boolean,
    default: false,
  },
});

const firstLetter = computed((): string => {
  const altValue = props.alt || "";
  return altValue.charAt(0);
});
</script>

<style lang="scss" scoped>
.ds-avatar {
  @apply flex items-center justify-center w-12 h-12 flex-shrink-0;

  &-letter {
    @apply bg-gray-900 text-white font-semibold uppercase text-2xl;
  }

  &-circle {
    @apply rounded-full;

    :deep(img),
    .ds-avatar-letter {
      @apply rounded-full;
    }
  }

  &-square {
    @apply rounded-md;

    :deep(img),
    .ds-avatar-letter {
      @apply rounded-md;
    }
  }

  &-xxs {
    @apply w-4 h-4;
    /*16px*/

    .ds-avatar-letter {
      @apply text-[8px];
    }

    &.ds-avatar-square {
      @apply rounded-[1px];

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-[1px];
      }
    }
  }

  &-xs {
    @apply w-6 h-6;
    /*24px*/

    .ds-avatar-letter {
      @apply text-xs;
    }

    &.ds-avatar-square {
      @apply rounded-sm;

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-sm;
      }
    }
  }

  &-sm {
    @apply w-8 h-8;
    /*32px*/

    .ds-avatar-letter {
      @apply text-base;
    }

    &.ds-avatar-square {
      @apply rounded-sm;

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-sm;
      }
    }
  }

  &-md {
    @apply w-12 h-12;
    /*48px*/

    .ds-avatar-letter {
      @apply text-lg;
    }

    &.ds-avatar-square {
      @apply rounded-sm;

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-sm;
      }
    }
  }

  &-lg {
    @apply w-[72px] h-[72px];
    /*72px*/

    .ds-avatar-letter {
      @apply text-3xl;
    }

    &.ds-avatar-square {
      @apply rounded-lg;

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-lg;
      }
    }
  }

  &-xl {
    @apply w-36 h-36;
    /*144px*/

    .ds-avatar-letter {
      @apply text-4xl;
    }

    &.ds-avatar-square {
      @apply rounded-xl;

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-xl;
      }
    }
  }

  &-xxl {
    @apply w-60 h-60;
    /*240px*/

    .ds-avatar-letter {
      @apply text-8xl;
    }

    &.ds-avatar-square {
      @apply rounded-xl;

      :deep(img),
      .ds-avatar-letter {
        @apply rounded-xl;
      }
    }
  }

  :deep(img) {
    @apply object-cover w-full h-full;
  }

  .ds-avatar-letter {
    @apply inline-block;
  }

  &-image-cloud {
    @apply w-full h-full;
  }

  &-skeleton {
    @apply w-full h-full;
  }
}
</style>
